import { TEST_HUB_URL } from '@/constants/constants';
import { Company } from '@/context/types';
import { Outlet, User, UserResponse } from '@/types';
import { Tax } from '@/types/taxes.type';
import { apiFetch } from '@/utils/apiWrapper';
import { BaseFilter, PaginationResponse } from '@/hooks/usePaginationFilter';
import { formatUrlWithFilters, getQueryParamsURL } from '@/utils/helpers/formatUrlWithFilters';

const usersUrl = `${TEST_HUB_URL}cdk/user/`;
export const sendVerificationCode = async (email: string) => {
  try {
    const response = await fetch(`${TEST_HUB_URL}user/send-verification-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error sending verification code:', error);
    throw error;
  }
};

export const checkVerification = async (email: string, code: string) => {
  try {
    const response = await fetch(`${TEST_HUB_URL}user/check-verification-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, code }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error verifiing code:', error);
    throw error;
  }
};

export const onBoardUser = async (
  user: UserResponse,
  company: Company,
  outlet: Partial<Outlet>,
  tax: Tax,
) => {
  const body = {
    user,
    company,
    outlet,
    tax,
  };
  try {
    const response = await fetch(`${TEST_HUB_URL}user/onboard`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error onboarding user:', error);
    throw error;
  }
};

export type UserQuery = {
  organizationId: string;
  filters?: UserFilters;
};

export type UserFilters = Partial<User> & BaseFilter;

export const getOrgUsers = async (query: UserQuery): Promise<PaginationResponse<User>> => {
  const { organizationId, filters } = query;
  try {
    let url = `${usersUrl}${organizationId}`;
    if (filters) {
      url = formatUrlWithFilters(url, filters);
    }
    const res = await apiFetch(url);
    if (!res) {
      throw new Error('Error fetching users');
    }
    const data = await res.json();
    return data as PaginationResponse<User>;
  } catch (err) {
    console.error('Error fetching company', err);
    throw new Error('Error fetching company');
  }
};

export const updateUser = async (
  userId: string,
  body: Partial<User>,
  organizationId: string,
  companyId: string,
): Promise<User | undefined> => {
  try {
    const response = await apiFetch(`${usersUrl}${organizationId}/${companyId}/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (response?.ok) {
      const data = await response.json();
      return data;
    }
  } catch (err) {
    console.error('Error updating user', err);
    throw new Error('Error updating user');
  }
};

export type CreateUserTypesRequest = {
  organizationId: string;
  body: Partial<User>;
  companyId: string;
};
export async function createUser({ organizationId, body, companyId }: CreateUserTypesRequest) {
  try {
    const response = await apiFetch(`${usersUrl}${organizationId}/${companyId}`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (response?.ok) {
      const data = await response.json();
      return data;
    }
  } catch (err) {
    throw new Error('Error creating user');
  }
}

export async function softUserDelete(userId: string, organizationId: string, companyId: string) {
  try {
    const body = {
      isDeleted: true,
    };
    const response = await apiFetch(`${usersUrl}${organizationId}/${companyId}/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (response?.ok) {
      const data = await response.json();
      return data;
    }
  } catch (err) {
    console.error('Error deleting user', err);
    throw new Error('Error deleting user');
  }
}

export const getUserById = async (userId: string, organizationId: string, companyId: string) => {
  try {
    const response = await apiFetch(`${usersUrl}get-user/${organizationId}/${companyId}/${userId}`);
    if (response?.ok) {
      const data = await response.json();
      return data;
    }
  } catch (err) {
    console.error('Error fetching user', err);
    throw new Error('Error fetching user');
  }
};

export const getUsersByQuery = async (
  organizationId: string,
  companyId: string,
  query: Partial<User>,
) => {
  try {
    const baseUrl = `${usersUrl}get-by-query/${organizationId}/${companyId}`;
    const url = getQueryParamsURL(baseUrl, query);

    const response = await apiFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (response?.ok) {
      const data = await response.json();
      return data;
    }

    throw new Error('Failed to fetch users');
  } catch (err) {
    console.error('Error fetching user', err);
    throw new Error('Error fetching user');
  }
};

export type CreateUserAuth0TypesRequest = {
  email: string;
  password: string;
  companyId: string;
  body: Partial<User>;
};

export async function createUserAuth0(payload: CreateUserAuth0TypesRequest) {
  try {
    const response = await apiFetch(`${usersUrl}create-user-auth0/${payload.companyId}`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (response?.ok) {
      const data = await response.json();
      return data;
    }
  } catch (err: any) {
    throw new Error((err?.message as string) || 'Error creating user');
  }
}
